import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMeetingEvidenceHistory } from '../../API/apiService';
import Loader from '../../components/loader';
import './style.scss';

const STATUS_LABELS = {
  pending: 'Pendiente de revisión',
  approved: 'Aprobado',
  needs_correction: 'Requiere subsanación'
};

function normalizeText(value) {
  return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function formatHistoryDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota', dateStyle: 'medium', timeStyle: 'short'
  }).format(date);
}

function getBogotaDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function ReviewerEvidenceHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    let mounted = true;
    getMeetingEvidenceHistory().then(result => {
      if (!mounted) return;
      setRecords(Array.isArray(result) ? result : []);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const filteredRecords = useMemo(() => {
    const term = normalizeText(search);
    return records.filter(record => {
      const matchesSearch = !term || normalizeText(`${record.mediatorName} ${record.groupCode} ${record.meetingId} ${record.incidenceActionName}`).includes(term);
      const recordDate = getBogotaDateKey(record.createdAt);
      const matchesDateFrom = !dateFrom || (recordDate && recordDate >= dateFrom);
      const matchesDateTo = !dateTo || (recordDate && recordDate <= dateTo);
      return matchesSearch && (!action || record.action === action) &&
        (!status || record.reviewStatus === status) && matchesDateFrom && matchesDateTo;
    });
  }, [records, search, action, status, dateFrom, dateTo]);

  return (
    <section className='reviewer-history'>
      <header className='reviewer-history__topbar'>
        <Link to='/revisor'><span className='material-symbols-rounded'>arrow_back</span>Centro de revisión</Link>
        <div><span className='material-symbols-rounded'>history</span>Historial</div>
      </header>
      <main className='reviewer-history__content'>
        <header className='reviewer-history__heading'>
          <div><span>ACTIVIDAD DE EVIDENCIAS</span><h1>Historial de cargas y ediciones</h1><p>Consulta lo que los mediadores han cargado o modificado, ordenado desde lo más reciente.</p></div>
          <strong>{filteredRecords.length} movimientos</strong>
        </header>

        <div className='reviewer-history__filters'>
          <label><span className='material-symbols-rounded'>search</span><input value={search} onChange={event => setSearch(event.target.value)} placeholder='Buscar mediador, grupo o meeting' /></label>
          <select value={action} onChange={event => setAction(event.target.value)} aria-label='Tipo de movimiento'>
            <option value=''>Todos los movimientos</option><option value='INITIAL_UPLOAD'>Cargas iniciales</option><option value='FILE_EDIT'>Ediciones</option>
          </select>
          <select value={status} onChange={event => setStatus(event.target.value)} aria-label='Estado de revisión'>
            <option value=''>Todos los estados</option><option value='pending'>Pendientes</option><option value='approved'>Aprobados</option><option value='needs_correction'>Requieren subsanación</option>
          </select>
          <label className='reviewer-history__date-filter'>
            <span>Desde</span>
            <input type='date' value={dateFrom} max={dateTo || undefined} onChange={event => setDateFrom(event.target.value)} />
          </label>
          <label className='reviewer-history__date-filter'>
            <span>Hasta</span>
            <input type='date' value={dateTo} min={dateFrom || undefined} onChange={event => setDateTo(event.target.value)} />
          </label>
        </div>

        {loading ? <div className='reviewer-history__loading'><Loader /></div> : (
          <div className='reviewer-history__list'>
            {filteredRecords.map(record => (
              <article key={record.id}>
                <span className={`reviewer-history__event-icon is-${record.action === 'INITIAL_UPLOAD' ? 'new' : 'edit'}`}><span className='material-symbols-rounded'>{record.action === 'INITIAL_UPLOAD' ? 'upload_file' : 'edit_document'}</span></span>
                <div className='reviewer-history__event-main'>
                  <div><strong>{record.mediatorName}</strong><span>{record.action === 'INITIAL_UPLOAD' ? 'realizó una carga inicial' : 'editó las evidencias'}</span></div>
                  <p><b>{record.evidenceType === 'concertacion' ? 'Concertación' : record.evidenceType === 'socializacion' ? `Socialización ${record.meetingOrder || 1}` : record.evidenceType === 'incidencia' ? (record.incidenceActionName || 'Acción de incidencia') : (record.groupCode || `Meeting ${record.meetingId}`)}</b>{record.evidenceType === 'sesion' ? ` · Sesión ${record.meetingOrder || '—'}` : record.evidenceType === 'incidencia' ? ` · ${record.groupCode || 'Sin grupo'}` : ` · ${record.schoolName || 'Institución'}`} · {record.filesChanged} {record.filesChanged === 1 ? 'archivo modificado' : 'archivos modificados'} · {record.fileCount} en total</p>
                  <small>{formatHistoryDate(record.createdAt)}</small>
                </div>
                <div className='reviewer-history__event-actions'>
                  <span className={`is-${record.reviewStatus}`}>{STATUS_LABELS[record.reviewStatus] || STATUS_LABELS.pending}</span>
                  {record.mediatorId && (record.groupId || record.schoolId || record.incidenceActionId) && (
                    <Link to={record.evidenceType === 'incidencia' ? '/revisor/evidencias-incidencia' : '/revisor/evidencias'} state={{evidenceTarget: {mediatorId: record.mediatorId, groupId: record.groupId, schoolId: record.schoolId, meetingId: record.meetingId, incidenceActionId: record.incidenceActionId, evidenceType: record.evidenceType}}}>Revisar evidencia<span className='material-symbols-rounded'>arrow_forward</span></Link>
                  )}
                </div>
              </article>
            ))}
            {!filteredRecords.length && <div className='reviewer-history__empty'><span className='material-symbols-rounded'>history_toggle_off</span><strong>Sin movimientos</strong><p>No hay registros que coincidan con los filtros seleccionados.</p></div>}
          </div>
        )}
      </main>
    </section>
  );
}

export default ReviewerEvidenceHistory;
